
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserRole } from "@/types/auth";
import { validatePassword, getPasswordErrorMessage, logSecurityEvent } from "@/utils/security";

export interface Usuario {
  id: string;
  email: string;
  nome: string;
  role: UserRole;
  user_role: UserRole;
  setor_departamento?: 'vendas' | 'financeiro' | 'tecnico' | 'administrativo';
  ativo: boolean;
  telefone?: string;
  created_at: string;
  updated_at?: string;
}

export function useUsuarios() {
  return useQuery({
    queryKey: ['usuarios'],
    queryFn: async () => {
      console.log('Buscando usuários...');
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('nome');

      if (error) {
        console.error('Erro ao buscar usuários:', error);
        throw error;
      }

      console.log('Usuários encontrados:', data);
      return data as Usuario[];
    }
  });
}

export function useCreateUsuario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (usuario: {
      email: string;
      password: string;
      nome: string;
      user_role: UserRole;
      setor_departamento?: 'vendas' | 'financeiro' | 'tecnico' | 'administrativo';
      telefone?: string;
    }) => {
      // Validar senha forte
      if (!validatePassword(usuario.password)) {
        const errorMessage = getPasswordErrorMessage(usuario.password);
        await logSecurityEvent('USER_CREATION_PASSWORD_VALIDATION_FAILED', { email: usuario.email, reason: errorMessage });
        throw new Error('Senha não atende aos requisitos de segurança. Verifique os requisitos abaixo.');
      }

      console.log('Criando usuário via signUp:', usuario);
      
      // Criar usuário através do signUp (isso criará automaticamente na auth.users)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: usuario.email,
        password: usuario.password,
        options: {
          data: {
            nome: usuario.nome,
            telefone: usuario.telefone,
            user_role: usuario.user_role,
            setor_departamento: usuario.setor_departamento
          }
        }
      });

      if (authError) {
        console.error('Erro ao criar usuário no auth:', authError);
        throw new Error('Erro ao criar usuário: ' + authError.message);
      }

      if (!authData.user) {
        throw new Error('Usuário não foi criado');
      }

      console.log('Usuário criado no auth:', authData.user.id);

      // Aguardar um pouco para o trigger processar
      console.log('⏳ Aguardando processamento do trigger...');
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Verificar se o perfil foi criado automaticamente pelo trigger
      const { data: profileCheck, error: profileCheckError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (profileCheckError) {
        console.error('Erro ao verificar perfil:', profileCheckError);
        
        // Se o perfil não foi criado pelo trigger, criar manualmente
        if (profileCheckError.code === 'PGRST116') {
          console.log('Perfil não encontrado, criando manualmente...');
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .insert({
              id: authData.user.id,
              email: usuario.email,
              nome: usuario.nome,
              user_role: usuario.user_role,
              role: usuario.user_role,
              setor_departamento: usuario.setor_departamento,
              telefone: usuario.telefone,
              ativo: true
            })
            .select()
            .single();

          if (profileError) {
            console.error('Erro ao criar perfil manualmente:', profileError);
            throw new Error('Erro ao criar perfil: ' + profileError.message);
          }

          return profileData;
        }
        
        throw new Error('Erro ao verificar perfil: ' + profileCheckError.message);
      }

      // Se o perfil existe, atualizar com os dados corretos
      console.log('Perfil encontrado, atualizando com dados corretos...');
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .update({
          user_role: usuario.user_role,
          role: usuario.user_role,
          setor_departamento: usuario.setor_departamento,
          telefone: usuario.telefone,
          nome: usuario.nome,
          ativo: true
        })
        .eq('id', authData.user.id)
        .select()
        .single();

      if (profileError) {
        console.error('Erro ao atualizar perfil:', profileError);
        throw new Error('Erro ao atualizar perfil: ' + profileError.message);
      }

      console.log('✅ Usuário criado e perfil atualizado com sucesso:', profileData);
      return profileData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success('Usuário criado com sucesso!');
    },
    onError: (error) => {
      console.error('Erro na mutação:', error);
      toast.error('Erro ao criar usuário: ' + error.message);
    }
  });
}

export function useUpdateUsuario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (usuario: {
      id: string;
      nome: string;
      email: string;
      role: UserRole;
      setor_departamento?: 'vendas' | 'financeiro' | 'tecnico' | 'administrativo';
      telefone?: string;
      ativo: boolean;
      novaSenha?: string;
    }) => {
      console.log('Atualizando usuário:', usuario);
      
      // Buscar email atual ANTES de atualizar para comparar corretamente
      const { data: currentProfile, error: currentProfileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', usuario.id)
        .single();

      if (currentProfileError) {
        console.error('Erro ao buscar email atual:', currentProfileError);
        throw currentProfileError;
      }

      const emailChanged = !!currentProfile && currentProfile.email !== usuario.email;
      const passwordChanged = !!(usuario.novaSenha && usuario.novaSenha.trim());

      // Validar senha forte se foi alterada
      if (passwordChanged && !validatePassword(usuario.novaSenha)) {
        const errorMessage = getPasswordErrorMessage(usuario.novaSenha);
        await logSecurityEvent('PASSWORD_VALIDATION_FAILED', { userId: usuario.id, reason: errorMessage });
        throw new Error('Nova senha não atende aos requisitos de segurança. Verifique os requisitos abaixo.');
      }

      // Atualizar perfil do usuário
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .update({
          nome: usuario.nome,
          email: usuario.email, // Incluir email na atualização
          role: usuario.role,
          user_role: usuario.role,
          setor_departamento: usuario.setor_departamento,
          telefone: usuario.telefone,
          ativo: usuario.ativo
        })
        .eq('id', usuario.id)
        .select()
        .single();

      if (profileError) {
        console.error('Erro ao atualizar perfil:', profileError);
        throw profileError;
      }

      // Se email ou senha foram alterados, usar a Edge Function (reset-password)
      if (emailChanged || passwordChanged) {
        const updateData: Record<string, unknown> = {
          targetUserId: usuario.id,
        };
        if (emailChanged) {
          updateData.newEmail = usuario.email;
        }
        if (passwordChanged && usuario.novaSenha) {
          updateData.newPassword = usuario.novaSenha;
        }

        const { data: authData, error: authError } = await supabase.functions.invoke('reset-password', {
          body: updateData,
        });

        if (authError) {
          console.error('Erro na Edge Function reset-password:', authError);
          throw new Error('Erro ao atualizar senha/e-mail: ' + (authError.message || 'verifique se a função reset-password está publicada no Supabase'));
        }

        if (authData && typeof authData === 'object' && 'error' in authData && authData.error) {
          const msg = typeof authData.error === 'string' ? authData.error : String(authData.error);
          throw new Error(msg);
        }

        if (authData && typeof authData === 'object' && (authData as { success?: boolean }).success) {
          if (passwordChanged) {
            toast.success('Senha atualizada com sucesso!');
          }
          if (emailChanged) {
            toast.success('E-mail atualizado com sucesso!');
          }
        }
      }

      return profileData;
    },
    onSuccess: (data) => {
      // Invalidar cache e atualizar dados
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      
      // Atualizar diretamente o cache se necessário
      queryClient.setQueryData(['usuarios'], (oldData: any) => {
        if (oldData) {
          return oldData.map((user: any) => 
            user.id === data.id ? { ...user, ...data } : user
          );
        }
        return oldData;
      });
      
      toast.success('Usuário atualizado com sucesso!');
    },
    onError: (error) => {
      console.error('Erro na mutação:', error);
      toast.error('Erro ao atualizar usuário: ' + error.message);
    }
  });
}

export function useUpdatePerfil() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (perfil: {
      id: string;
      nome: string;
      telefone?: string;
      setor_departamento?: 'vendas' | 'financeiro' | 'tecnico' | 'administrativo';
    }) => {
      console.log('Atualizando perfil:', perfil);
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .update({
          nome: perfil.nome,
          telefone: perfil.telefone || null,
          setor_departamento: perfil.setor_departamento,
        })
        .eq('id', perfil.id)
        .select()
        .single();

      if (profileError) {
        console.error('Erro ao atualizar perfil:', profileError);
        throw profileError;
      }

      return profileData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success('Perfil atualizado com sucesso!');
    },
    onError: (error) => {
      console.error('Erro na mutação:', error);
      toast.error('Erro ao atualizar perfil');
    }
  });
}
